import React, { useCallback, useEffect, useRef, useState } from "react";

import { twJoin } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";

import {
  TextEditor,
  TextEditorRef,
} from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { MAX_COMMENT_LENGTH } from "src/cases/caseView/constants";
import { useCreateCaseComment } from "src/cases/queries/history";
import { Button } from "src/design-system/Button";
import { Pill } from "src/design-system/Pill";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { CasePageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseCommentEditor: React.FC = () => {
  const { case_id: caseId, wsId } = useParamsDecode<CasePageParams>();
  const { mutateAsync: createCaseComment, isPending } = useCreateCaseComment({
    caseId,
  });

  const [comment, setComment, removeCommentFromStorage] = useLocalStorage(
    `caseComment-${wsId}-${caseId}`,
    "",
  );

  const handleSubmit = async () => {
    const trimmedComment = comment.trim();

    if (trimmedComment === "") {
      toastActions.failure({
        title: "Failed to add comment",
        description: "Comment cannot be empty",
      });
      return;
    }

    if (trimmedComment.length > MAX_COMMENT_LENGTH) {
      toastActions.failure({
        title: "Failed to add comment",
        description: `Comment must be at most ${MAX_COMMENT_LENGTH.toLocaleString()} characters`,
      });
      return;
    }

    try {
      await createCaseComment({ comment });
      removeCommentFromStorage();
    } catch (error) {
      logger.error(error);
      toastActions.failure({
        title: "Failed to add comment",
        description: "Please try again later",
      });
    }
  };

  const handleChange = useCallback(
    (value: string) => {
      if (value === "") {
        removeCommentFromStorage();
      } else {
        setComment(value);
      }
    },
    [removeCommentFromStorage, setComment],
  );

  return (
    <CommentEditor
      dataLoc="case-comment-editor"
      isPending={isPending}
      value={comment}
      autoFocus
      onChange={handleChange}
      onSubmit={handleSubmit}
    />
  );
};

type CommentEditorProps = {
  dataLoc?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  autoFocus?: boolean;
  isPending?: boolean;
  value: string;
};

const CommentEditor: React.FC<CommentEditorProps> = ({
  dataLoc,
  value,
  onChange,
  autoFocus = false,
  isPending = false,
  onSubmit,
}) => {
  const toolbarRef = useRef<HTMLUListElement>(null);
  const editorRef = useRef<TextEditorRef>(null);

  const characterCount = useCommentCharacterCount(editorRef, value);
  const remainingCharacters = MAX_COMMENT_LENGTH - characterCount;
  const isEditorEmpty = characterCount === 0;
  const isOverLimit = characterCount > MAX_COMMENT_LENGTH;

  return (
    <div
      className={twJoin(
        "flex flex-col rounded-lg border border-gray-200 bg-white p-3",
        "focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-500/25",
        "group",
      )}
    >
      <TextEditor
        ref={editorRef}
        autoFocus={autoFocus}
        dataLoc={dataLoc}
        placeholder="Add your comment here..."
        toolbarRef={toolbarRef}
        value={value}
        keepEmptyLines
        onChange={onChange}
      />
      <div className="flex items-center justify-between gap-x-3 pt-3">
        <Toolbar ref={toolbarRef} exclude={["heading"]} />
        <div className="relative flex shrink-0 items-center gap-x-3">
          {isOverLimit && (
            <div className="absolute right-0 bottom-full mb-2">
              <Pill
                dataLoc={`${dataLoc}-character-count`}
                size="sm"
                variant="red"
              >
                <Pill.Text>{remainingCharacters.toLocaleString()}</Pill.Text>
              </Pill>
            </div>
          )}
          <Tooltip
            asChild
            disabled={!isOverLimit}
            placement="top"
            title={`Comment must be at most ${MAX_COMMENT_LENGTH.toLocaleString()} characters`}
          >
            <Button
              dataLoc={`${dataLoc}-save-button`}
              isDisabled={isEditorEmpty || isOverLimit}
              isLoading={isPending}
              size="sm"
              variant="primary"
              onClick={onSubmit}
            >
              Add comment
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

// Counts the submitted markdown
const useCommentCharacterCount = (
  editorRef: React.RefObject<TextEditorRef | null>,
  initialValue: string,
) => {
  const [characterCount, setCharacterCount] = useState(
    initialValue.trim().length,
  );

  useEffect(() => {
    const quill = editorRef.current?.quill;
    if (!quill) return;

    const handleTextChange = () => {
      // Blank content can serialize to non-empty markdown (e.g. "&nbsp;").
      const isBlank = quill.getText().trim().length === 0;
      setCharacterCount(
        isBlank ? 0 : (editorRef.current?.getMarkdownValue().length ?? 0),
      );
    };

    handleTextChange();
    quill.on("text-change", handleTextChange);

    return () => {
      quill.off("text-change", handleTextChange);
    };
  }, [editorRef]);

  return characterCount;
};
